import {
  createEventSeries,
  deleteEvent,
  fetchAllEventsWithFinance,
  fetchPackageRates,
  fetchPackages,
  fetchProducts,
  saveEvent,
  saveEventFinance,
} from "../lib/api.js";
import { COLLECTION_STATES, collectionOf, eventCostTotal, eventIncome, eventProfit, needsQuote } from "../lib/calc.js";
import { formatCurrency, formatDate, formatTimeRange, toLocalISODate } from "../lib/format.js";
import { confirmDialog, escapeHtml, openModal, showError } from "../lib/ui.js";
import { eventFormHtml, wireEventForm } from "./eventForm.js";
import { openEventDetail } from "./eventDetail.js";

export async function renderCalendar(container) {
  container.innerHTML = `<h2>Calendario de eventos</h2><p>Cargando…</p>`;

  try {
    const [events, products, packages, packageRates] = await Promise.all([
      fetchAllEventsWithFinance(),
      fetchProducts(),
      fetchPackages(),
      fetchPackageRates(),
    ]);
    const reload = () => renderCalendar(container);

    // Los coordinadores no son usuarios: la lista sale de lo ya cargado.
    const coordinators = [...new Set(events.map((e) => e.assigned_coordinator).filter(Boolean))].sort();

    const today = toLocalISODate();

    // Filtros en memoria: con cientos de eventos, una lista pelada es inusable.
    const filters = { text: "", productId: "", coordinator: "", collection: "", from: "", to: "", pendingOnly: false };

    const matches = (e) => {
      if (filters.productId && e.product_id !== filters.productId) return false;
      if (filters.coordinator && e.assigned_coordinator !== filters.coordinator) return false;
      if (filters.collection && collectionOf({ ...e, ...(e.finance ?? {}) }, today).state !== filters.collection) return false;
      if (filters.from && e.date < filters.from) return false;
      if (filters.to && e.date > filters.to) return false;
      if (filters.pendingOnly && e.finance) return false;
      if (filters.text) {
        const haystack = [
          e.description,
          products.find((p) => p.id === e.product_id)?.name,
          packages.find((p) => p.id === e.package_id)?.name,
          e.notes,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(filters.text.toLowerCase())) return false;
      }
      return true;
    };

    const pendingCount = events.filter((e) => !e.finance).length;

    // Resumen de cobranza de todo lo listado, para no tener que abrir cumple
    // por cumple. Misma regla que la ficha y el panel (collectionOf).
    const cobranza = { senado: 0, cobrado: 0, debe: 0, sin_sena: 0, falta: 0 };
    for (const e of events) {
      if (!e.finance) continue;
      const c = collectionOf({ ...e, ...e.finance }, today);
      cobranza[c.state] += 1;
      cobranza.falta += c.pending;
    }

    const draw = () => {
      const visible = events.filter(matches);

      container.innerHTML = `
        <div id="view">
        <h2>Calendario de eventos</h2>

        ${
          pendingCount > 0
            ? `<div class="notice">
                 <strong>${pendingCount} evento(s) sin revisar.</strong>
                 Los cargó un empleado y todavía no tienen números.
                 <button class="icon-btn" id="show-pending">Ver sólo esos</button>
               </div>`
            : ""
        }

        <div class="filters">
          <input type="search" id="f-text" placeholder="Buscar por nombre, producto o nota…" value="${escapeHtml(filters.text)}" />
          <select id="f-product">
            <option value="">Todos los productos</option>
            ${products
              .map(
                (p) => `<option value="${escapeHtml(p.id)}"${filters.productId === p.id ? " selected" : ""}>${escapeHtml(p.name)}</option>`
              )
              .join("")}
          </select>
          ${
            coordinators.length > 0
              ? `<select id="f-coordinator">
                  <option value="">Cualquier coordinador</option>
                  ${coordinators
                    .map((c) => `<option value="${escapeHtml(c)}"${filters.coordinator === c ? " selected" : ""}>${escapeHtml(c)}</option>`)
                    .join("")}
                </select>`
              : ""
          }
          <select id="f-collection">
            <option value="">Cualquier cobranza</option>
            <option value="senado"${filters.collection === "senado" ? " selected" : ""}>Señados (falta el saldo)</option>
            <option value="debe"${filters.collection === "debe" ? " selected" : ""}>Deben</option>
            <option value="cobrado"${filters.collection === "cobrado" ? " selected" : ""}>Cobrados</option>
            <option value="sin_sena"${filters.collection === "sin_sena" ? " selected" : ""}>Sin seña</option>
          </select>
          <input type="date" id="f-from" value="${escapeHtml(filters.from)}" aria-label="Desde" />
          <input type="date" id="f-to" value="${escapeHtml(filters.to)}" aria-label="Hasta" />
          <button class="icon-btn" id="clear-filters">Limpiar</button>
        </div>

        <p class="collection-summary">
          <span class="badge-state senado">${cobranza.senado} señados</span>
          <span class="badge-state cobrado">${cobranza.cobrado} cobrados</span>
          ${cobranza.debe ? `<span class="badge-state debe">${cobranza.debe} deben</span>` : ""}
          ${cobranza.sin_sena ? `<span class="badge-state sin_sena">${cobranza.sin_sena} sin seña</span>` : ""}
          <span class="hint">· falta cobrar ${formatCurrency(cobranza.falta)}</span>
        </p>

        <div class="toolbar">
          <span>${visible.length} de ${events.length} evento(s)</span>
          <button class="btn" id="new-event">+ Nuevo evento</button>
        </div>

        <div class="panel">
          ${
            visible.length === 0
              ? "<p>No hay eventos que coincidan.</p>"
              : `<div class="table-wrap"><table class="table-cards">
                  <thead>
                    <tr><th>Fecha</th><th>Evento</th><th>Producto</th><th>Chicos</th><th>Precio</th><th>Cobranza</th><th>Costos</th><th>Ganancia</th><th></th></tr>
                  </thead>
                  <tbody>
                    ${visible.map((e) => rowHtml(e, products, today)).join("")}
                  </tbody>
                </table></div>`
          }
        </div>
        </div>
      `;

      const view = container.querySelector("#view");

      view.querySelector("#new-event").addEventListener("click", () => {
        openEventModal({ products, packages, packageRates, coordinators }, null, reload);
      });

      view.querySelector("#show-pending")?.addEventListener("click", () => {
        filters.pendingOnly = !filters.pendingOnly;
        draw();
      });

      const applyFilter = (key, el, immediate = false) => {
        el.addEventListener(immediate ? "change" : "input", () => {
          filters[key] = el.value;
          draw();
          // el buscador pierde el foco al redibujar; se lo devolvemos
          if (!immediate) {
            const again = container.querySelector(`#${el.id}`);
            again.focus();
            again.setSelectionRange(again.value.length, again.value.length);
          }
        });
      };

      applyFilter("text", view.querySelector("#f-text"));
      applyFilter("productId", view.querySelector("#f-product"), true);
      if (view.querySelector("#f-coordinator")) applyFilter("coordinator", view.querySelector("#f-coordinator"), true);
      applyFilter("collection", view.querySelector("#f-collection"), true);
      applyFilter("from", view.querySelector("#f-from"), true);
      applyFilter("to", view.querySelector("#f-to"), true);

      view.querySelector("#clear-filters").addEventListener("click", () => {
        filters.text = "";
        filters.productId = "";
        filters.coordinator = "";
        filters.collection = "";
        filters.from = "";
        filters.to = "";
        filters.pendingOnly = false;
        draw();
      });

      view.addEventListener("click", async (e) => {
        const editId = e.target.dataset?.edit;
        const deleteId = e.target.dataset?.delete;

        // Clic en cualquier parte de la fila que no sea un botón: abre la ficha.
        if (!editId && !deleteId) {
          const row = e.target.closest("[data-event-row]");
          if (row) {
            const event = events.find((ev) => ev.id === row.dataset.eventRow);
            openEventDetail(event, { products, packages }, {
              onEdit: (ev) => openEventModal({ products, packages, packageRates, coordinators }, ev, reload),
            });
          }
          return;
        }

        if (editId) {
          openEventModal({ products, packages, packageRates, coordinators }, events.find((ev) => ev.id === editId), reload);
        }

        if (deleteId && confirmDialog("¿Seguro que querés borrar este evento? También se borran sus movimientos automáticos.")) {
          await deleteEvent(deleteId);
          reload();
        }
      });
    };

    draw();
  } catch (error) {
    showError(container, error);
  }
}

function rowHtml(e, products, today) {
  const finance = e.finance;
  const withCounts = { ...e, ...(finance ?? {}) };
  const cost = finance ? eventCostTotal(withCounts) : null;
  const profit = finance ? eventProfit(withCounts) : null;
  const c = finance ? collectionOf(withCounts, today) : null;
  const cobranza = !c
    ? "—"
    : `<span class="badge-state ${c.state}">${COLLECTION_STATES[c.state][0]}</span>
       <span class="hint">${c.pending > 0 ? `falta ${formatCurrency(c.pending)}` : "completo"}</span>`;

  return `<tr data-event-row="${escapeHtml(e.id)}" class="clickable-row" title="Ver la ficha del evento">
    <td data-label="Fecha">${formatDate(e.date)}${
      e.start_time ? `<span class="hint" style="display:block">${formatTimeRange(e.start_time, e.end_time)}</span>` : ""
    }</td>
    <td data-label="Evento">
      ${escapeHtml(e.description)}
      ${finance ? "" : '<span class="badge pending">sin revisar</span>'}
      ${finance && needsQuote(finance.cost_lines) ? '<span class="badge-state debe">falta presupuesto</span>' : ""}
    </td>
    <td data-label="Producto">${escapeHtml(products.find((p) => p.id === e.product_id)?.name ?? "—")}</td>
    <td data-label="Chicos">${e.children_count}</td>
    <td data-label="Precio">${finance ? formatCurrency(eventIncome(withCounts)) : "—"}</td>
    <td data-label="Cobranza">${cobranza}</td>
    <td data-label="Costos">${finance ? formatCurrency(cost) : "—"}</td>
    <td data-label="Ganancia" class="${finance ? (profit >= 0 ? "value positive" : "value negative") : ""}" style="font-size: 14px">
      ${finance ? formatCurrency(profit) : "—"}
    </td>
    <td data-label="">
      <div class="row-actions">
        <button class="icon-btn" data-edit="${escapeHtml(e.id)}">${finance ? "Editar" : "Revisar"}</button>
        <button class="icon-btn danger" data-delete="${escapeHtml(e.id)}">Borrar</button>
      </div>
    </td>
  </tr>`;
}

export function openEventModal({ products, packages, packageRates, coordinators = [] }, event, onSaved) {
  const modal = openModal(`
    <button class="modal-close" type="button" data-close>✕</button>
    <h3>${event ? "Editar evento" : "Nuevo evento"}</h3>
    ${eventFormHtml({ products, packages, event, finance: event?.finance ?? null, includeFinance: true, coordinators })}
  `);

  modal.querySelector("[data-close]").addEventListener("click", () => modal.close());

  wireEventForm(modal, {
    products,
    packages,
    packageRates,
    event,
    includeFinance: true,
    onCancel: () => modal.close(),
    onSubmit: async ({ operational, financial, repeat }) => {
      // Serie repetida: se crean todos los eventos de una, cada uno con su
      // propia parte financiera, para poder ajustarlos por separado después.
      if (repeat && repeat.dates.length > 1) {
        const { eventIds } = await createEventSeries(
          {
            description: operational.description,
            product_id: operational.product_id,
            frequency: repeat.frequency,
            until_date: repeat.until,
          },
          repeat.dates,
          operational
        );
        if (financial) {
          await Promise.all(eventIds.map((id) => saveEventFinance(id, financial)));
        }
        modal.close();
        onSaved?.();
        return;
      }

      const eventId = await saveEvent(operational, event?.id ?? null);
      if (financial) await saveEventFinance(eventId, financial);
      modal.close();
      onSaved?.();
    },
  });
}
